import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, { Background } from 'reactflow';
import { CanvasContextMenu } from '@components/MainStage/ContextMenu/CanvasContextMenu';
import { CustomControls } from '@components/MainStage/CustomControls';
import { CustomEdge } from '@components/MainStage/CustomEdge';
import { CustomNode } from '@components/MainStage/CustomNode';
import useCanvasContextMenu from '@components/MainStage/hooks/stage/useCanvasContextMenu';
import useDragNode from '@components/MainStage/hooks/stage/useDragNode';
import useEdgeHoverHandlers from '@components/MainStage/hooks/stage/useEdgeHoverHandlers';
import useHoverPointerReset from '@components/MainStage/hooks/stage/useHoverPointerReset';
import useTranslateExtent from '@components/MainStage/hooks/stage/useTranslateExtent';
import useEdgeLayers from '@components/MainStage/hooks/useEdgeLayers';
import useFilteredCanvas from '@components/MainStage/hooks/useFilteredCanvas';
import { getHoverState } from '@components/MainStage/hover.store';
import { LayerNode } from '@components/MainStage/LayerNode';
import { BlockBackground } from '@components/MainStage/MainStage.styled';
import { NewNode } from '@components/MainStage/NewNode';
import useStore from '@components/MainStage/store';
import { SubLayerNode } from '@components/MainStage/SubLayerNode';
import { MenuState } from '@components/MainStage/types';
import {
  canvasStageStyles,
  INITIAL_VIEWPORT,
  MAX_ZOOM,
  MIN_ZOOM,
} from '@constants/canvas/general';
import { ReactFlowId } from '@constants/entities/reports';
import { NodesDataProvider } from '@context/NodesDataContext';
import { useProject } from '@context/Project/ProjectProvider';
import useCanvasZoom from '@hooks/canvas/useCanvasZoom';
import useCanvasZoomController from '@hooks/canvas/useCanvasZoomController';
import useCanEditModel from '@hooks/useCanEditModel';
import useToggle from '@hooks/useToggle';
import { Box } from '@mui/material';
import { InsightStageStyled } from '@views/Insights/Insights.styled';
import useEdgeClick from '@views/Insights/Stage/hooks/stage/useEdgeClick';
import useNodeClick from '@views/Insights/Stage/hooks/stage/useNodeClick';
import usePaneClick from '@views/Insights/Stage/hooks/stage/usePaneClick';
import { Tabs } from '@views/Insights/types';

const nodeTypes = {
  customNode: CustomNode,
  newNode: NewNode,
  layerNode: LayerNode,
  subLayerNode: SubLayerNode,
};

const edgeTypes = {
  customEdge: CustomEdge,
};

const defaultEdgeOptions = { type: 'customEdge' };

export const Stage = () => {
  const { handleCloseDrawer, hasChanges, insightsTab: type } = useProject();
  const { nodes, edges: filteredEdges } = useFilteredCanvas();
  const edges = useEdgeLayers(filteredEdges);
  const onNodesChange = useStore((state) => state.onNodesChange);
  const { zoom } = useCanvasZoom();
  const canEdit = useCanEditModel();

  const [menu, setMenu] = useState<MenuState | null>(null);
  const [isMenuOpen, { off: closeMenu, on: openMenu }] = useToggle();

  const { onCanvasContextMenu } = useCanvasContextMenu({ openMenu, setMenu });

  const handlePaneClick = usePaneClick();

  const onPaneClick = useCallback(() => {
    closeMenu();
    handlePaneClick();
  }, [closeMenu, handlePaneClick]);

  const onMove = useCallback(() => {
    if (menu) closeMenu();
  }, [menu, closeMenu]);

  const onEdgeClick = useEdgeClick(onPaneClick);
  const onNodeClick = useNodeClick(onPaneClick);
  const { onEdgeMouseEnter, onEdgeMouseLeave } = useEdgeHoverHandlers();

  const { onMouseOver: onHoverPointerOver, onMouseLeave: onHoverPointerOut } =
    useHoverPointerReset();

  // The hover store is module-level, so a hover left behind here would follow
  // the user to the model canvas.
  useEffect(() => () => getHoverState().clear(), []);

  // Secure-by-Design keeps the model editable, so its nodes drag and persist
  // like on the model canvas. The other insights stay read-only.
  const isNodeDragEnabled = type === Tabs.CIA && canEdit;

  const { onNodeDragStart, onNodeDrag, onNodeDragStop } = useDragNode({
    menu,
    closeMenu,
  });

  const onBlockBackClick = () => {
    if (type === Tabs.Dependency) {
      handleCloseDrawer();
    } else if (type === Tabs.FocusOn) {
      handleCloseDrawer(undefined, false);
    }
  };

  useCanvasZoomController();
  const translateExtent = useTranslateExtent();

  return (
    <Box
      id="canvas"
      position="relative"
      onMouseOver={onHoverPointerOver}
      onMouseLeave={onHoverPointerOut}
    >
      {hasChanges && <BlockBackground onClick={onBlockBackClick} />}

      <InsightStageStyled>
        <NodesDataProvider>
          <ReactFlow
            id={ReactFlowId}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            elevateNodesOnSelect
            selectNodesOnDrag={false}
            onMove={onMove}
            onPaneClick={onPaneClick}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onEdgeMouseEnter={onEdgeMouseEnter}
            onEdgeMouseLeave={onEdgeMouseLeave}
            onPaneContextMenu={onCanvasContextMenu}
            onNodeContextMenu={onCanvasContextMenu}
            onEdgeContextMenu={onCanvasContextMenu}
            nodesDraggable={isNodeDragEnabled}
            nodeDragThreshold={1}
            onNodesChange={isNodeDragEnabled ? onNodesChange : undefined}
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            style={canvasStageStyles}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
            deleteKeyCode={null}
            translateExtent={translateExtent}
            zoomOnDoubleClick={false}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            zoomOnPinch={false}
            defaultViewport={INITIAL_VIEWPORT(zoom)}
          >
            <Background />

            <CanvasContextMenu
              isOpen={isMenuOpen}
              onClose={closeMenu}
              menu={menu}
            />

            <CustomControls />
          </ReactFlow>
        </NodesDataProvider>
      </InsightStageStyled>
    </Box>
  );
};
